import { AddressLinkDropdownPreview } from '@Components/AddressLinkDropdownPreview';
import { EntityAttachmentsButton } from '@Components/Attachments/EntityAttachmentsButton';
import { LinkedFieldWithLayoutSettings } from '@Components/LinkedFieldWithLayoutSettings';
import { EmailPreviewModal } from '@Components/Mailing';
import { PositionsTable } from '@Components/Tables/PositionsTable';
import { AUFTRAG_API } from '@Constants/backendConstants';
import { Permission } from '@Constants/Permission.g';
import { addressColumns, orderPositionColumns, vehicleColumns } from '@Constants/TableColumns';
import { useHasPermission } from '@Contexts/appContext';
import { ScalesProvider } from '@Contexts/scales';
import { useInvoicePrintPreview, usePrintOptions } from '@Hooks/Documents';
import { usePrepareEmail } from '@Hooks/Mailing';
import { EnumWeighing, useOrderPermissions, useOrderTotals, useWeighingActions } from '@Hooks/Orders';
import { useCanCollectOrder } from '@Hooks/Orders/useCanCollectOrder';
import { useOrderAttachments } from '@Hooks/Orders/useOrderAttachments';
import { useBadges } from '@Hooks/useBadges';
import {
    type ApiAddress,
    type ApiOrder,
    type ApiOrderPosition,
    type ApiVehicle,
    EnumFrontendLayoutSetting,
    EnumMaterialFlow,
    EnumOrderType,
    EnumProcessState,
    materialFlowOptions,
    orderTypeOptions,
    SendDeliverySlipRequest,
    type SendPrepareInvoiceRequest,
} from '@Model';
import { PrintOptionsMenu, PrintOptionsModal } from '@Modules/Documents';
import {
    CashPaymentModal,
    OrderPosition,
    RemarksCard,
    useLastWeighedOrder,
    WeighingModal,
    WeighingSlipSelectionModal,
} from '@Modules/Orders';
import { FrameEditAddress, FrameEditVehicle } from '@Modules/Stammdaten';
import {
    Badge,
    Button,
    Card,
    DateInput,
    defaultIconSize,
    EditFrameMode,
    EditFrameShell,
    EnumStatus,
    FlexRow,
    FrameEditBase,
    Grid,
    GridColumn,
    GridColumns,
    type IFrameBodyProps,
    type Index,
    isLinked,
    LabeledDiv,
    renderCurrency,
    Select,
    SingleSelect,
    useEditSubObject,
    useFrameEditBaseNavigation,
    useGuiBinder,
    useLinkedFieldInFrame,
    useModalState,
    useTranslate,
} from '@synqo/frontend-component-library';
import { IconCashBanknote, IconCurrencyEuro, IconNotes } from '@tabler/icons-react';
import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router';
import { useClientNavigate } from '../../../../../Routing/useClientNavigate';

const remarkTypes: [Index<ApiOrder>, string][] = [
    ['remarks', 'Auftrag'],
    ['scale_remarks', 'Waage'],
    ['invoice_remarks', 'Rechnung'],
];

function noOpSave() {
    return true;
}

export function FrameWeighing() {
    const hasPermission = useHasPermission();
    const { itemId } = useParams();
    const primaryKey = itemId ?? '';

    const frameEditBaseNavigationEvents = useFrameEditBaseNavigation();

    const frameProps = useMemo(
        () => ({
            events: frameEditBaseNavigationEvents,
        }),
        [frameEditBaseNavigationEvents],
    );

    return (
        <ScalesProvider>
            <FrameEditBase<ApiOrder>
                key={primaryKey}
                primaryKey={primaryKey}
                embedded={false}
                apiResourcePath={`${AUFTRAG_API}/orders`}
                renderer={FrameWeighingBody}
                canEdit={hasPermission(Permission.AuftragsbearbeitungOrdersUpdate)}
                canCreate={hasPermission(Permission.AuftragsbearbeitungOrdersCreate)}
                canDelete={hasPermission(Permission.AuftragsbearbeitungOrdersDelete)}
                frameProps={frameProps}
            />
        </ScalesProvider>
    );
}

function FrameWeighingBody(props: IFrameBodyProps<ApiOrder>) {
    const i18n = useTranslate('');
    const navigate = useClientNavigate();
    const hasPermission = useHasPermission();
    const canEditOrder = hasPermission(Permission.AuftragsbearbeitungOrdersUpdate);
    const { setLastWeighedOrder } = useLastWeighedOrder();

    const apiOrder = props.data;
    const baseApiObjectPath = `${props.apiResourcePath}/${apiOrder.id}`;

    const {
        isOpen: isCashPaymentModalOpen,
        open: openCashPaymentModal,
        close: closeCashPaymentModal,
    } = useModalState();

    const isCashPayment = apiOrder.type === EnumOrderType.Cash;
    const {
        collectible,
        canCollect,
        canApprove,
        actionText: collectionActionText,
        tooltip: collectButtonTooltip,
    } = useCanCollectOrder(apiOrder);

    const isInViewMode = props.mode === EditFrameMode.View;
    const isNotInViewMode = props.mode !== EditFrameMode.View;
    const canDelete = apiOrder.process_state !== EnumProcessState.Completed;

    const weighingSlips = useMemo(() => apiOrder.positions.flatMap((pos) => pos.weighing_slips), [apiOrder.positions]);
    const weighingSlipsWithoutTare = useMemo(
        () => weighingSlips.filter((ws) => ws.tare_weight === 0).map((ws) => ws.id),
        [weighingSlips],
    );
    const weighingSlipsWithoutGross = useMemo(
        () => weighingSlips.filter((ws) => ws.gross_weight === 0).map((ws) => ws.id),
        [weighingSlips],
    );

    const conditionalEvents = useMemo(
        () => ({
            ...props.events,
            onDelete: canDelete ? props.events.onDelete : undefined,
        }),
        [props.events, canDelete],
    );

    const { forceReadOnly: forceReadOnlyCallback } = useOrderPermissions(apiOrder, {
        allowedPropertiesWhenCompleted: ['remarks', 'process_state'],
    });

    const binder = useGuiBinder<ApiOrder>({
        ...props,
        canEdit: isNotInViewMode,
        forceReadOnly: forceReadOnlyCallback,
    });

    const { totalGrossPrice } = useOrderTotals(apiOrder);

    const printOptionInfo = usePrintOptions('order', apiOrder.id, isInViewMode && apiOrder.status !== EnumStatus.Draft);
    const { currentPrintOption, setCurrentPrintOption } = printOptionInfo;
    const previewInvoice = useInvoicePrintPreview(printOptionInfo);

    const attachmentsInfo = useOrderAttachments(apiOrder.id);
    const canManageAttachments = isInViewMode && hasPermission(Permission.AuftragsbearbeitungOrdersAttachmentsManage);

    const linkCustomer = useLinkedFieldInFrame(props, 'customer', FrameEditAddress);
    const linkCarrier = useLinkedFieldInFrame(props, 'carrier', FrameEditAddress);
    const linkPickUp = useLinkedFieldInFrame(props, 'pickup_site', FrameEditAddress);

    const linkVehicle = useLinkedFieldInFrame(props, 'vehicle', FrameEditVehicle);
    const linkTrailer = useLinkedFieldInFrame(props, 'trailer', FrameEditVehicle);

    const requestUpdateState = props.requestUpdateState;
    const saveAndCommit = props.events.onSave ?? noOpSave;

    const unlinkCustomer = useCallback(
        () => requestUpdateState(linkCustomer.linkApi.unlink),
        [requestUpdateState, linkCustomer.linkApi.unlink],
    );

    const {
        addSubObject: addPosition,
        deleteSubObjects: deletePositionsHandler,
        requestUpdateSubObject: requestUpdatePosition,
    } = useEditSubObject<ApiOrderPosition, ApiOrder>(baseApiObjectPath, 'positions', requestUpdateState);

    const orderBadges = useBadges(apiOrder);

    const onStartWeighing = useCallback(() => setLastWeighedOrder(apiOrder.id), [setLastWeighedOrder, apiOrder.id]);

    const {
        startWeighingIn,
        startWeighingOut,
        weighInNewPosition,
        saveAndNavigateToHofliste,
        approveAndPrint,
        weighingModalState,
        closeWeighingModal,
        handleWeighingSlipSelection,
        closeWeighingSlipSelectionModal,
    } = useWeighingActions({
        apiOrder,
        weighingSlipsWithoutGross,
        weighingSlipsWithoutTare,
        onStartWeighing,
        addPosition,
        setCurrentPrintOption,
        openCashPaymentModal,
        saveAndCommit,
        requestUpdateState,
        deletePositionsHandler,
    });

    const {
        isPreparing: isDeliverySlipPreparing,
        emailRequest,
        setEmailRequest,
        prepareEmail,
    } = usePrepareEmail<SendDeliverySlipRequest>('/prepare_delivery_slip_email');

    const {
        isPreparing: isInvoicePreparing,
        emailRequest: invoiceEmailRequest,
        setEmailRequest: setInvoiceEmailRequest,
        prepareEmail: prepareInvoiceEmail,
    } = usePrepareEmail<SendPrepareInvoiceRequest>('/prepare_invoice_email');

    const positionInWeighing = apiOrder.positions.find((pos) => pos.id === weighingModalState?.orderPositionId);

    const isWeighingSlipSelectionModalOpen =
        weighingModalState !== undefined && weighingModalState.orderPositionId === undefined;

    const positionColumn = useMemo(() => orderPositionColumns(apiOrder.taxation_mode), [apiOrder.taxation_mode]);

    const sameCustomerAndPickupSite =
        isLinked(apiOrder.pickup_site) &&
        isLinked(apiOrder.customer) &&
        apiOrder.pickup_site.foreign_key === apiOrder.customer.foreign_key;

    const canWeighIn = isNotInViewMode && apiOrder.process_state !== EnumProcessState.Completed;
    const canWeighOut =
        isNotInViewMode &&
        weighingSlipsWithoutTare.length >= 1 &&
        apiOrder.process_state !== EnumProcessState.Completed;

    const approveOrder = useCallback(async () => {
        const saveResult = await saveAndCommit();
        if (!saveResult) {
            return false;
        }

        return requestUpdateState((latest, saveData) => {
            return saveData({ ...latest, process_state: EnumProcessState.Approved });
        });
    }, [saveAndCommit, requestUpdateState]);

    return (
        <EditFrameShell
            headerProps={{
                label: 'Auftrag verwiegen',
                resourceLabel: 'Auftrag',
                ...conditionalEvents,
                actionItems: [
                    canEditOrder && canApprove && (
                        <Button key="approve" appearance="filled" onClick={approveOrder} variant="secondary">
                            Freigabe für Faktura
                        </Button>
                    ),
                    canEditOrder && collectible && (
                        <Button
                            key="cash_payment"
                            appearance="filled"
                            onClick={openCashPaymentModal}
                            locked={!canCollect}
                            tooltip={collectButtonTooltip}
                            variant="secondary"
                        >
                            <IconCurrencyEuro size={defaultIconSize} />
                            &nbsp;{i18n(collectionActionText)}
                        </Button>
                    ),
                ],
                menuItems: [
                    [
                        <Button
                            key="hofliste"
                            onClick={() => navigate('../list?mode=hofliste')}
                            appearance={'outlined'}
                            locked={isNotInViewMode}
                        >
                            <IconNotes size={defaultIconSize} />
                            &nbsp;Hofliste
                        </Button>,
                    ],
                    <PrintOptionsMenu
                        key="print_menu"
                        {...printOptionInfo}
                        disabled={props.mode !== EditFrameMode.View}
                    />,
                    hasPermission(Permission.AuftragsbearbeitungOrdersAttachmentsRead) && (
                        <EntityAttachmentsButton
                            key="attachments"
                            attachmentsInfo={attachmentsInfo}
                            canUpload={canManageAttachments}
                            canDownload={isInViewMode}
                            canDelete={canManageAttachments}
                        />
                    ),
                ],
            }}
            bannerProps={{
                mode: props.mode,
                loading: props.stateQueueDepth > 0,
                ...conditionalEvents,
            }}
        >
            <Card
                key="main"
                name="main"
                label={'Auftrag & Fahrzeuginformationen'}
                badges={orderBadges}
                collapsable={false}
                border={'withoutBorder'}
            >
                <Grid>
                    <GridColumns spans={[5, 5, 2]}>
                        <Select<EnumMaterialFlow>
                            key="material_flow"
                            items={materialFlowOptions}
                            {...binder.bindToAutoConfirmed('material_flow')}
                            label="Stoffweg"
                        />
                        <SingleSelect
                            items={orderTypeOptions}
                            {...binder.bindToAutoConfirmed<EnumOrderType>('type')}
                            label={'Wiegetyp'}
                            fullWidth
                        />
                        {apiOrder.type === EnumOrderType.Cash && (
                            <LabeledDiv htmlFor="cashAmount" label="Bar-Betrag">
                                <Badge variant="info" size="sm">
                                    <IconCashBanknote size={defaultIconSize} />
                                    &nbsp;
                                    {renderCurrency(totalGrossPrice, 2)}
                                </Badge>
                            </LabeledDiv>
                        )}
                    </GridColumns>
                </Grid>
                <Grid>
                    <GridColumn span={6}>
                        <DateInput {...binder.bindToProp('completed_at')} label={'Erledigungsdatum'} />
                    </GridColumn>
                </Grid>
                <Grid>
                    <GridColumns spans={[6, 6]}>
                        <LinkedFieldWithLayoutSettings<ApiVehicle>
                            layoutSettingsContext={EnumFrontendLayoutSetting.VehiclesTableLayout}
                            key="vehicle"
                            {...binder.bindToLinkProperty(linkVehicle.linkApi)}
                            {...linkVehicle.events}
                            columns={vehicleColumns}
                            label={i18n('Fahrzeug')}
                        />
                        <LinkedFieldWithLayoutSettings<ApiVehicle>
                            layoutSettingsContext={EnumFrontendLayoutSetting.VehiclesTableLayout}
                            key="trailer"
                            {...binder.bindToLinkProperty(linkTrailer.linkApi)}
                            {...linkTrailer.events}
                            columns={vehicleColumns}
                            label={i18n('Anhänger')}
                        />
                    </GridColumns>
                </Grid>
            </Card>

            <Card name="technical" key="technical" label={'Adresse'}>
                <Grid>
                    <GridColumns spans={sameCustomerAndPickupSite ? [4, 'content', 4] : [4, 4, 4]}>
                        <LinkedFieldWithLayoutSettings<ApiAddress>
                            layoutSettingsContext={EnumFrontendLayoutSetting.AddressesTableLayout}
                            key="pickup_site"
                            {...binder.bindToLinkProperty(linkPickUp.linkApi)}
                            {...linkPickUp.events}
                            columns={addressColumns}
                            label={i18n('Herkunft')}
                            dropdownPreview={AddressLinkDropdownPreview}
                            highlighted={isNotInViewMode}
                        />
                        {sameCustomerAndPickupSite ? (
                            <FlexRow key="deviatingCustomer" justify="start">
                                <LabeledDiv label={'Kunde'} htmlFor={'ToggleShowClient'}>
                                    <Button onClick={unlinkCustomer} appearance={'outlined'} locked={isInViewMode}>
                                        Abweichende Rechnungsadresse
                                    </Button>
                                </LabeledDiv>
                            </FlexRow>
                        ) : (
                            <LinkedFieldWithLayoutSettings<ApiAddress>
                                layoutSettingsContext={EnumFrontendLayoutSetting.AddressesTableLayout}
                                key="customer"
                                {...binder.bindToLinkProperty(linkCustomer.linkApi)}
                                {...linkCustomer.events}
                                columns={addressColumns}
                                dropdownPreview={AddressLinkDropdownPreview}
                                label={i18n('Kunde')}
                            />
                        )}
                        <LinkedFieldWithLayoutSettings<ApiAddress>
                            layoutSettingsContext={EnumFrontendLayoutSetting.AddressesTableLayout}
                            key="carrier"
                            {...binder.bindToLinkProperty(linkCarrier.linkApi)}
                            {...linkCarrier.events}
                            columns={addressColumns}
                            label={i18n('Beförderer')}
                            dropdownPreview={AddressLinkDropdownPreview}
                        />
                    </GridColumns>
                </Grid>
            </Card>
            <Card name="actions" key="actions" label={'Waage'}>
                <Grid>
                    <GridColumns spans={[6, 6]}>
                        <FlexRow grow>
                            <Button onClick={startWeighingIn} appearance={'filled'} locked={!canWeighIn}>
                                {weighingSlips.length < 1 ? 'Einwiegung' : 'Weitere Einwiegung'}
                            </Button>
                        </FlexRow>
                        <FlexRow grow>
                            <Button
                                onClick={startWeighingOut}
                                appearance={'filled'}
                                variant="secondary"
                                locked={!canWeighOut}
                            >
                                Auswiegung
                            </Button>
                        </FlexRow>
                    </GridColumns>
                </Grid>
            </Card>

            <RemarksCard remarkTypes={remarkTypes} data={apiOrder} canEdit={isNotInViewMode} binder={binder} />

            <Card name="positions" label={`Positionen (${apiOrder.positions.length})`} border={'withoutBorder'}>
                <PositionsTable<ApiOrderPosition>
                    layoutSettingsContext={EnumFrontendLayoutSetting.OrderPositionsTableLayout}
                    items={apiOrder.positions}
                    columns={positionColumn}
                    initialSorting={{
                        columnName: 'number',
                        direction: 'asc',
                    }}
                    name={'LinkedOrderPositions'}
                    onAdd={addPosition}
                    onDelete={deletePositionsHandler}
                    editable={isNotInViewMode && !forceReadOnlyCallback('positions')}
                    canCreate={isNotInViewMode && !forceReadOnlyCallback('positions')}
                    renderSubComponent={(row) => (
                        <OrderPosition
                            key={row.id}
                            data={row}
                            meta={undefined}
                            apiResourcePath={`${baseApiObjectPath}/positions`}
                            processState={apiOrder.process_state}
                            priceDisplay={apiOrder.taxation_mode}
                            refetchData={props.refetchData}
                            requestUpdateState={(u) => requestUpdatePosition(u, row.id)}
                            stateQueueDepth={props.stateQueueDepth}
                            canEdit={!isInViewMode && !forceReadOnlyCallback('positions')}
                        />
                    )}
                />
            </Card>

            {weighingModalState !== undefined && positionInWeighing !== undefined && (
                <WeighingModal
                    key={weighingModalState.orderPositionId}
                    isOpen={isNotInViewMode && weighingModalState !== undefined}
                    onClose={closeWeighingModal}
                    data={positionInWeighing}
                    meta={undefined}
                    weighingSlipId={weighingModalState.weighingSlipId}
                    apiResourcePath={`${baseApiObjectPath}/positions`}
                    priceDisplay={apiOrder.taxation_mode}
                    weighingType={weighingModalState.mode}
                    isCashPayment={isCashPayment}
                    refetchData={props.refetchData}
                    requestUpdateState={(u) => requestUpdatePosition(u, weighingModalState.orderPositionId ?? '')}
                    stateQueueDepth={props.stateQueueDepth}
                    doAnotherWeighing={
                        weighingModalState.mode === EnumWeighing.Tare ? startWeighingOut : startWeighingIn
                    }
                    saveAndNavigateToHofliste={saveAndNavigateToHofliste}
                    approveAndPrint={approveAndPrint}
                    canEdit={isNotInViewMode}
                />
            )}
            <WeighingSlipSelectionModal
                isOpen={isWeighingSlipSelectionModalOpen}
                onClose={closeWeighingSlipSelectionModal}
                orderPositions={apiOrder.positions}
                mode={weighingModalState?.mode ?? EnumWeighing.Gross}
                onSelect={handleWeighingSlipSelection}
                weighInNewPosition={weighInNewPosition}
            />
            <CashPaymentModal
                type="order"
                entity={apiOrder}
                currency="€"
                totalGrossPrice={totalGrossPrice}
                isOpen={isCashPaymentModalOpen}
                close={closeCashPaymentModal}
                onBeforeStartPayment={approveOrder}
                onSuccessfulPayment={(invoices) => {
                    void props.refetchData();
                    const invoiceId = invoices[0]?.id;
                    if (invoiceId === undefined) {
                        return;
                    }
                    //ToDo: modify for multiple invoices
                    previewInvoice(invoiceId);
                }}
                requestUpdateState={requestUpdateState}
            />
            <PrintOptionsModal
                printOption={currentPrintOption}
                sendAsEmail={
                    currentPrintOption?.id === 'invoice'
                        ? () => prepareInvoiceEmail({ invoice_id: currentPrintOption.related_object_id })
                        : () => prepareEmail({ order_id: apiOrder.id })
                }
                onClose={() => setCurrentPrintOption(undefined)}
            />
            <EmailPreviewModal
                isPreparing={isDeliverySlipPreparing}
                emailRequest={emailRequest}
                setEmailRequest={setEmailRequest}
            />
            <EmailPreviewModal
                isPreparing={isInvoicePreparing}
                emailRequest={invoiceEmailRequest}
                setEmailRequest={setInvoiceEmailRequest}
            />
        </EditFrameShell>
    );
}
